import { Box, Text, TextProps, VisuallyHidden } from '@everlywell/ui-kit';
import React from 'react';

export type PriceProps = Pick<
  TextProps,
  'color' | 'fontSize' | 'fontWeight' | 'size' | 'textAlign'
> & {
  // the total price of the product
  price: number;
  strikeThrough?: boolean;

  /** It is the relationship between the dollars and the cents font size
   * @default 16/24
   */
  ratio?: number;
};

/**
 * Price component used to display the price of a product.
 */
function Price(props: PriceProps): JSX.Element {
  const { price, strikeThrough, ratio = 16 / 24, ...rest } = props;
  const [dollars, cents] = price.toFixed(2).split('.');

  // We use the ratio to calculate the font size of the cents
  // The font size of the cents is always proportional to the font size of the dollars
  const fontSize = `max(${ratio}em, 12px)`;

  return (
    <Text
      fontSize="3xl"
      fontWeight="medium"
      {...rest}
      as="div"
      role={strikeThrough ? 'deletion' : undefined}
    >
      <VisuallyHidden>
        {strikeThrough ? 'Before ' : null} {dollars} dollars and{' '}
        {cents !== '00' ? `${cents} cents` : 'no cents'}
      </VisuallyHidden>
      <Box aria-hidden whiteSpace="pre" position="relative" lineHeight={1}>
        {strikeThrough ? (
          <Text
            as="span"
            fontSize={fontSize}
            fontWeight="inherit"
            color="inherit"
            lineHeight={1}
            position="absolute"
            bottom="0.5px"
            width="100%"
            sx={{
              ':before': {
                content: '"$"',
              },
              ':after': {
                content: '""',
                display: 'block',
                width: '105%',
                height: '0.08em',
                background: 'utility.sale',
                borderRadius: '0.08em',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              },
            }}
          ></Text>
        ) : null}
        <Text
          as="span"
          fontSize={fontSize}
          fontWeight="inherit"
          color="inherit"
          lineHeight={1}
        >
          $
        </Text>
        <Text
          as="span"
          fontSize="1em"
          fontWeight="inherit"
          color="inherit"
          lineHeight={1}
        >
          {dollars}
        </Text>
        <Text
          as="span"
          fontSize={fontSize}
          fontWeight="inherit"
          color="inherit"
          lineHeight={1}
        >
          .{cents}
        </Text>
      </Box>
    </Text>
  );
}

export default Price;
